import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { allProducts, productCategories } from '@/mocks/products';
import { useCart } from '@/context/CartContext';
import type { ProductSize } from '@/mocks/products';
import SEO from '@/components/SEO';

// Map product category value -> collection display name
const categoryToCollection: Record<string, string> = {
  Spices: 'Garam Masala',
  Flour: 'Organic Flour',
  Oils: 'Organic Oils',
  Seeds: 'Organic Seeds',
  Masalas: 'Recipe Masalas',
  Superfoods: 'Super Foods',
  Remedies: 'Remedies',
  Honey: 'Honey',
};

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://pearlfoods.pk';

const ProductDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { addItem } = useCart();

  const product = allProducts.find((p) => p.id === Number(id));

  const categoryTrustBadge = product
    ? productCategories.find((c) => {
        const map: Record<string, string> = {
          'Garam Masala': 'Spices',
          'Organic Flour': 'Flour',
          'Organic Oils': 'Oils',
          'Organic Seeds': 'Seeds',
          'Recipe Masalas': 'Masalas',
          'Super Foods': 'Superfoods',
          'Remedies': 'Remedies',
          'Honey': 'Honey',
        };
        return map[c.name] === product.category;
      })?.trustBadge ?? ''
    : '';

  const [selectedSize, setSelectedSize] = useState<ProductSize | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [addedFeedback, setAddedFeedback] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (product) setSelectedSize(product.sizes[1] ?? product.sizes[0]);
  }, [product]);

  // Determine the collection this product belongs to
  const collectionName = product ? (categoryToCollection[product.category] ?? 'All') : 'All';
  const collectionUrl = collectionName === 'All'
    ? '/products'
    : `/products?category=${encodeURIComponent(collectionName)}`;

  // Determine where back button should go:
  // If we came from the products page (with or without category param), go back there.
  // Otherwise navigate to the product's own collection.
  const handleBack = () => {
    const referrer = (location.state as { from?: string } | null)?.from;
    if (referrer && referrer.startsWith('/products')) {
      navigate(referrer);
    } else if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate(collectionUrl);
    }
  };

  const isContactForPrice = selectedSize?.contactForPrice === true;

  const relatedProducts = allProducts
    .filter((p) => p.category === product?.category && p.id !== product?.id)
    .slice(0, 3);

  const jsonLd = product
    ? [
        {
          '@context': 'https://schema.org',
          '@type': 'Product',
          name: product.name,
          description: product.fullDescription,
          image: product.image,
          url: `${siteUrl}/products/${product.id}`,
          brand: {
            '@type': 'Brand',
            name: 'Pearl Foods',
          },
          category: product.category,
          ...(product.benefits.length > 0 && {
            hasHealthConsideration: product.benefits.map((b) => ({
              '@type': 'HealthCondition',
              name: b,
            })),
          }),
          ...(selectedSize && !isContactForPrice && {
            offers: {
              '@type': 'Offer',
              priceCurrency: 'PKR',
              price: selectedSize.price,
              availability: 'https://schema.org/InStock',
              url: `${siteUrl}/products/${product.id}`,
              itemOffered: {
                '@type': 'Product',
                name: `${product.name} - ${selectedSize.label}`,
              },
            },
          }),
          ...(isContactForPrice && {
            offers: {
              '@type': 'Offer',
              priceCurrency: 'PKR',
              availability: 'https://schema.org/InStock',
              url: `${siteUrl}/products/${product.id}`,
            },
          }),
          aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: '5',
            reviewCount: '12',
          },
        },
      ]
    : [];

  if (!product) {
    return (
      <div>
        <SEO
          title="Product Not Found | Pearl Foods"
          description="The product you are looking for does not exist or may have been removed."
          noindex
        />
        <Navbar />
        <div className="min-h-screen flex flex-col items-center justify-center bg-brand-cream px-4">
          <div className="w-16 h-16 flex items-center justify-center rounded-full bg-brand-beige mb-4">
            <i className="ri-search-line text-2xl text-brand-brown/40"></i>
          </div>
          <h2 className="font-serif text-2xl text-brand-brown-dark font-bold mb-2">Product Not Found</h2>
          <p className="font-sans text-brand-brown/60 text-sm mb-6">This product doesn&apos;t exist or may have been removed.</p>
          <Link
            to="/products"
            className="font-sans text-sm font-medium bg-brand-olive text-white px-6 py-3 rounded-full hover:bg-brand-olive-dark transition-colors cursor-pointer whitespace-nowrap"
          >
            Back to Products
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const handleAddToCart = () => {
    if (!selectedSize || selectedSize.contactForPrice) return;
    addItem(product, selectedSize);
    setAddedFeedback(true);
    setTimeout(() => setAddedFeedback(false), 2000);
  };

  return (
    <div className="bg-brand-cream min-h-screen">
      <SEO
        title={`${product.name} | Pearl Foods Islamabad`}
        description={product.description}
        keywords={`${product.name}, ${product.category}, organic ${product.category.toLowerCase()}, Pearl Foods, ${product.benefit}`}
        canonicalPath={`/products/${product.id}`}
        image={product.image}
        jsonLd={jsonLd}
      />
      <Navbar />

      {/* Breadcrumb */}
      <div className="pt-24 md:pt-28 pb-0 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <nav className="flex items-center gap-2 font-sans text-xs text-brand-brown/50">
            <Link to="/" className="hover:text-brand-olive transition-colors cursor-pointer">Home</Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to={collectionUrl} className="hover:text-brand-olive transition-colors cursor-pointer">{collectionName}</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-brand-brown">{product.name}</span>
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <section className="py-10 md:py-14 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">

            {/* Product Image */}
            <div>
              <div className="relative rounded-2xl overflow-hidden bg-brand-beige/20 w-full" style={{ aspectRatio: '1/1' }}>
                <img
                  src={product.image}
                  alt={product.name}
                  className="absolute inset-0 w-full h-full object-contain p-6"
                />
                <span className="absolute top-4 left-4 bg-brand-olive text-white text-xs font-sans font-medium px-3 py-1.5 rounded-full">
                  {product.tag}
                </span>
              </div>

              {/* Category badge */}
              <div className="flex items-center gap-2 mt-4">
                <span className="font-sans text-xs text-brand-brown/50 bg-brand-beige px-3 py-1.5 rounded-full">
                  {product.category}
                </span>
                <span className="font-sans text-xs text-brand-olive bg-brand-olive/10 px-3 py-1.5 rounded-full flex items-center gap-1">
                  <i className="ri-leaf-line text-xs"></i>
                  100% Organic
                </span>
                <span className="font-sans text-xs text-brand-brown/50 bg-brand-beige px-3 py-1.5 rounded-full flex items-center gap-1">
                  <i className="ri-hand-heart-line text-xs"></i>
                  Handmade
                </span>
              </div>
            </div>

            {/* Product Info */}
            <div className="flex flex-col">
              {/* Back button */}
              <button
                onClick={handleBack}
                className="hidden lg:flex items-center gap-1.5 text-brand-brown/50 hover:text-brand-olive transition-colors font-sans text-sm mb-6 cursor-pointer w-fit"
              >
                <i className="ri-arrow-left-line"></i>
                Back
              </button>

              <h1 className="font-serif text-3xl md:text-4xl text-brand-brown-dark font-bold mb-2">
                {product.name}
              </h1>

              <p className="font-sans text-sm text-brand-olive italic mb-4">{product.benefit}</p>

              <p className="font-sans text-sm text-brand-brown/70 leading-relaxed mb-4">
                {product.fullDescription}
              </p>

              {categoryTrustBadge && (
                <div className="flex items-center gap-2 mb-6 bg-brand-olive/8 border border-brand-olive/20 rounded-xl px-4 py-2.5">
                  <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 text-brand-olive">
                    <i className="ri-leaf-line text-sm"></i>
                  </div>
                  <span className="font-sans text-xs text-brand-olive font-medium">{categoryTrustBadge}</span>
                </div>
              )}

              {/* Health Benefits */}
              <div className="bg-white rounded-2xl p-5 mb-6">
                <h3 className="font-serif text-sm font-semibold text-brand-brown-dark mb-3 flex items-center gap-2">
                  <i className="ri-heart-pulse-line text-brand-olive"></i>
                  Health Benefits
                </h3>
                <ul className="space-y-2">
                  {product.benefits.map((b, i) => (
                    <li key={i} className="flex items-start gap-2.5">
                      <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-checkbox-circle-line text-brand-olive text-sm"></i>
                      </div>
                      <span className="font-sans text-xs text-brand-brown/80 leading-relaxed">{b}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Size Selector */}
              <div className="mb-5">
                <h3 className="font-sans text-xs font-semibold text-brand-brown/70 uppercase tracking-wider mb-2.5">
                  Select Size
                </h3>
                <div className="flex flex-wrap gap-2">
                  {product.sizes.map((size) => (
                    <button
                      key={size.label}
                      onClick={() => setSelectedSize(size)}
                      className={`font-sans text-sm px-4 py-2 rounded-full border transition-all duration-200 cursor-pointer whitespace-nowrap ${
                        selectedSize?.label === size.label
                          ? 'border-brand-olive bg-brand-olive text-white'
                          : 'border-brand-beige text-brand-brown bg-white hover:border-brand-olive/50'
                      }`}
                    >
                      {size.label}
                      {!size.contactForPrice && (
                        <span className="ml-2 font-medium">
                          Rs. {size.price.toLocaleString()}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Price Display */}
              {selectedSize && (
                <div className="mb-5">
                  {isContactForPrice ? (
                    <span className="font-serif text-2xl font-bold text-brand-brown-dark">
                      Contact for Price
                    </span>
                  ) : (
                    <>
                      <span className="font-serif text-3xl font-bold text-brand-brown-dark">
                        Rs. {selectedSize.price.toLocaleString()}
                      </span>
                      <span className="font-sans text-sm text-brand-brown/50 ml-2">per {selectedSize.label}</span>
                    </>
                  )}
                </div>
              )}

              {/* Quantity Selector */}
              {!isContactForPrice && (
                <div className="flex items-center gap-4 mb-6">
                  <div>
                    <h3 className="font-sans text-xs font-semibold text-brand-brown/70 uppercase tracking-wider mb-2.5">
                      Quantity
                    </h3>
                    <div className="flex items-center gap-1 bg-white rounded-full border border-brand-beige p-1">
                      <button
                        onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                        className="w-8 h-8 flex items-center justify-center rounded-full bg-brand-beige hover:bg-brand-beige/70 text-brand-brown transition-colors cursor-pointer"
                        aria-label="Decrease quantity"
                      >
                        <i className="ri-subtract-line text-sm"></i>
                      </button>
                      <span className="font-sans font-semibold text-brand-brown-dark w-10 text-center text-base">
                        {quantity}
                      </span>
                      <button
                        onClick={() => setQuantity((q) => q + 1)}
                        className="w-8 h-8 flex items-center justify-center rounded-full bg-brand-beige hover:bg-brand-beige/70 text-brand-brown transition-colors cursor-pointer"
                        aria-label="Increase quantity"
                      >
                        <i className="ri-add-line text-sm"></i>
                      </button>
                    </div>
                  </div>

                  {selectedSize && (
                    <div className="mt-6">
                      <span className="font-sans text-xs text-brand-brown/50">Subtotal</span>
                      <p className="font-serif text-lg font-bold text-brand-olive">
                        Rs. {(selectedSize.price * quantity).toLocaleString()}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Add to Cart / WhatsApp CTA */}
              {isContactForPrice ? (
                <a
                  href={`https://wa.me/923445299984?text=I'm%20interested%20in%20${encodeURIComponent(product.name)}.%20Please%20share%20the%20price.`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2.5 font-sans font-medium text-base py-4 rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap bg-[#25D366] hover:bg-[#20b858] text-white"
                >
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-whatsapp-line text-lg"></i>
                  </div>
                  Ask Price on WhatsApp
                </a>
              ) : (
                <button
                  onClick={handleAddToCart}
                  disabled={!selectedSize}
                  className={`flex items-center justify-center gap-2.5 font-sans font-medium text-base py-4 rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap ${
                    addedFeedback
                      ? 'bg-brand-gold text-white'
                      : 'bg-brand-olive hover:bg-brand-olive-dark text-white'
                  }`}
                >
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className={addedFeedback ? 'ri-check-line' : 'ri-shopping-basket-2-line'}></i>
                  </div>
                  {addedFeedback ? 'Added to Cart!' : 'Add to Cart'}
                </button>
              )}

              {/* Trust Badges */}
              <div className="flex flex-wrap gap-4 mt-6 pt-5 border-t border-brand-beige">
                {[
                  { icon: 'ri-leaf-line', text: '100% Organic' },
                  { icon: 'ri-hand-heart-line', text: 'Handmade' },
                  { icon: 'ri-shield-check-line', text: 'No Chemicals' },
                  { icon: 'ri-truck-line', text: 'Islamabad Delivery' },
                ].map((badge) => (
                  <div key={badge.text} className="flex items-center gap-1.5">
                    <div className="w-4 h-4 flex items-center justify-center text-brand-olive">
                      <i className={`${badge.icon} text-sm`}></i>
                    </div>
                    <span className="font-sans text-xs text-brand-brown/60">{badge.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Products */}
      {relatedProducts.length > 0 && (
        <section className="py-14 px-4 md:px-8 bg-white">
          <div className="max-w-7xl mx-auto">
            <div className="mb-8">
              <span className="font-sans text-xs tracking-[0.25em] uppercase text-brand-olive font-medium">
                From {product.category}
              </span>
              <h2 className="font-serif text-2xl md:text-3xl text-brand-brown-dark font-bold mt-1">
                You May Also Like
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              {relatedProducts.map((rel) => (
                <Link
                  key={rel.id}
                  to={`/products/${rel.id}`}
                  className="group bg-brand-cream rounded-2xl overflow-hidden hover:-translate-y-1 transition-transform duration-300 cursor-pointer block"
                >
                  <div className="w-full overflow-hidden bg-brand-beige/30" style={{ aspectRatio: '1/1' }}>
                    <img
                      src={rel.image}
                      alt={rel.name}
                      className="w-full h-full object-contain p-4 group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-4">
                    <h3 className="font-serif text-brand-brown-dark font-semibold text-base mb-0.5">
                      {rel.name}
                    </h3>
                    <p className="font-sans text-xs text-brand-olive italic mb-2">{rel.benefit}</p>
                    <span className="font-serif text-sm font-semibold text-brand-brown-dark">
                      From Rs. {rel.sizes[0].price.toLocaleString()}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
};

export default ProductDetailPage;